import { useCallback, useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Clock, CreditCard, RefreshCw, XCircle } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MacJitLogo from "../components/MacJitLogo";
import api from "../lib/api";

export default function CheckoutStatusPage() {
  const { id } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/public/checkout/${id}`);
      setCheckout(res.data || {});
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const queryStatus = new URLSearchParams(location.search).get("status") || "";
  const status = String(queryStatus || checkout?.status || "payment_pending").toLowerCase();
  const active = status === "active";
  const failed = status.includes("failed") || status.includes("cancel");
  const pending = !active && !failed;
  const Icon = active ? CheckCircle2 : failed ? XCircle : Clock;
  const isMini = String(checkout?.plan || "").toLowerCase() === "terminal_ledger" || String(checkout?.plan_name || "").toLowerCase() === "mini";
  const productName = isMini ? "Mini" : "Terminal";
  const productLoginPath = isMini ? "/mini" : "/terminal";
  const productLoginUrl = `${window.location.origin}${productLoginPath}`;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <main className="mx-auto grid min-h-screen max-w-4xl place-items-center px-6 py-12">
        <section className="w-full border border-zinc-800 bg-zinc-900/40 p-6 sm:p-10">
          <div className="flex items-center gap-3">
            <MacJitLogo size={38} />
            <div>
              <p className="font-display text-xl font-black tracking-tighter">MACJIT <span className="text-orange-500">/ CHECKOUT</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Buy to {productName} delivery</p>
            </div>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_280px] lg:items-start">
            <div>
              <Icon className={`h-12 w-12 ${active ? "text-emerald-400" : failed ? "text-red-400" : "text-orange-500"}`} />
              <h1 className="mt-5 font-display text-4xl font-black uppercase tracking-tighter">
                {active ? `Your ${productName} is ready.` : failed ? "Checkout needs help." : "Payment is being verified."}
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
                {active
                  ? `Your account has been onboarded. Your temporary password and ${productName} access link were shared with the registered email used during onboarding.`
                  : failed
                    ? (checkout?.payment_gateway_error || `The payment did not complete. You can retry the payment link or return to ${productName} and subscribe again.`)
                    : `If you just completed payment, this page may take a moment while Razorpay returns confirmation and ${productName} creates your account.`}
              </p>
              {active && (
                <div className="mt-5 space-y-3 border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                  <p>Check the onboarding mailbox{checkout?.owner_email ? ` (${checkout.owner_email})` : ""}. Use that temporary password on {productName}, then set a new password on first login.</p>
                  <div className="grid gap-2 border border-emerald-500/20 bg-black/40 p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-100/90">
                    <p>Login URL: <span className="normal-case tracking-normal text-white">{productLoginUrl}</span></p>
                    <p>Registered email: <span className="normal-case tracking-normal text-white">{checkout?.owner_email || "-"}</span></p>
                    <p>Temporary password: <span className="normal-case tracking-normal text-white">Sent to registered email</span></p>
                  </div>
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 border border-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
                <button onClick={() => nav(productLoginPath)} className="inline-flex items-center gap-2 bg-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400">
                  Open {productName} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-5">
              <CreditCard className="h-5 w-5 text-orange-500" />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Checkout</p>
              <p className="mt-2 font-display text-2xl font-black">{checkout?.plan_name || "Terminal"}</p>
              <p className="mt-1 font-display text-4xl font-black text-orange-500">₹{checkout?.amount ?? "-"}</p>
              <div className="mt-5 space-y-2 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                <p>ID: {id}</p>
                <p>Status: {checkout?.status || "loading"}</p>
                <p>Payment: {checkout?.payment_status || "pending"}</p>
                <p>Company: {checkout?.tenant_name || "-"}</p>
              </div>
              {(pending || failed) && checkout?.payment_url && (
                <a href={checkout.payment_url} className="mt-5 flex items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400">
                  {failed ? "Retry Payment" : "Continue Payment"}
                </a>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
